import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios, { clearApiCache, getCacheStats } from '../lib/axios';
import API_CONFIG from '../lib/api-config';
import toast from 'react-hot-toast';

// Context for API state management
const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [apiStatus, setApiStatus] = useState('idle');
  const [lastSync, setLastSync] = useState(null);
  const [cacheStats, setCacheStats] = useState({ size: 0, keys: [] });

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('🟢 חיבור לאינטרנט חודש');
      // Refresh data when coming back online
      refreshAllData();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('🔴 אין חיבור לאינטרנט');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Health check every 30 seconds
  useEffect(() => {
    const healthCheck = async () => {
      try {
        // השתמש בפרוקסי במקום לגשת ישירות לשרת
        const response = await fetch('/health');
        if (response.ok) {
          setApiStatus('healthy');
          setLastSync(new Date());
        } else {
          setApiStatus('error');
          console.error('Health check failed: response not ok');
        }
      } catch (error) {
        setApiStatus('error');
        console.error('Health check failed:', error);
      }
    };

    // Initial health check
    healthCheck();

    // Periodic health check
    const interval = setInterval(healthCheck, 30000);

    return () => clearInterval(interval);
  }, []);

  // Update cache stats every 10 seconds
  useEffect(() => {
    const updateCacheStats = () => {
      setCacheStats(getCacheStats());
    };

    updateCacheStats();
    const interval = setInterval(updateCacheStats, 10000);

    return () => clearInterval(interval);
  }, []);

  // Enhanced API functions with better error handling
  const apiCall = useCallback(async (method, url, data = null, options = {}) => {
    if (!isOnline) {
      throw new Error(API_CONFIG.ERROR_MESSAGES.NETWORK_ERROR);
    }

    setApiStatus('loading');

    try {
      const config = {
        method,
        url,
        ...options
      };

      if (data) {
        config.data = data;
      }

      const response = await axios(config);
      
      // בדוק אם התגובה היא JSON
      if (response.data && typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        throw new Error('השרת החזיר HTML במקום JSON - ייתכן שיש בעיה עם ה-proxy');
      }
      
      setApiStatus('success');
      setLastSync(new Date());
      console.log("response", response);
      return response.data;
    } catch (error) {
      setApiStatus('error');
      
      // Handle specific error types
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || API_CONFIG.ERROR_MESSAGES.SERVER_ERROR;
        
        // בדוק אם התגובה היא HTML במקום JSON
        if (typeof error.response.data === 'string' && error.response.data.includes('<!doctype html>')) {
          toast.error('שגיאת proxy - השרת החזיר HTML במקום JSON');
          throw new Error('שגיאת proxy - השרת החזיר HTML במקום JSON');
        }
        
        switch (status) {
          case 401:
            toast.error(API_CONFIG.ERROR_MESSAGES.UNAUTHORIZED);
            break;
          case 403:
            toast.error(API_CONFIG.ERROR_MESSAGES.FORBIDDEN);
            break;
          case 404:
            toast.error(API_CONFIG.ERROR_MESSAGES.NOT_FOUND);
            break;
          case 422:
            toast.error(API_CONFIG.ERROR_MESSAGES.VALIDATION_ERROR);
            break;
          case 500:
            toast.error(API_CONFIG.ERROR_MESSAGES.SERVER_ERROR);
            break;
          default:
            toast.error(message);
        }
      } else if (error.code === 'ECONNABORTED') {
        toast.error(API_CONFIG.ERROR_MESSAGES.TIMEOUT_ERROR);
      } else {
        toast.error(API_CONFIG.ERROR_MESSAGES.NETWORK_ERROR);
      }
      
      throw error;
    }
  }, [isOnline]);

  // Convenience methods
  const get = useCallback((url, options = {}) => apiCall('get', url, null, options), [apiCall]);
  const post = useCallback((url, data, options = {}) => apiCall('post', url, data, options), [apiCall]);
  const put = useCallback((url, data, options = {}) => apiCall('put', url, data, options), [apiCall]);
  const del = useCallback((url, options = {}) => apiCall('delete', url, null, options), [apiCall]);
  const patch = useCallback((url, data, options = {}) => apiCall('patch', url, data, options), [apiCall]);

  // Refresh all cached data
  const refreshAllData = async () => {
    try {
      clearApiCache();
      toast.success('🔄 נתונים עודכנו');
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Real-time data fetching with caching
  const fetchWithCache = async (url, options = {}) => {
    try {
      const response = await get(url, options);
      return response;
    } catch (error) {
      // If network error, try to return cached data
      if (!isOnline) {
        console.log('Offline mode - using cached data if available');
        // You could implement offline storage here
      }
      throw error;
    }
  };

  // Batch API calls
  const batchCall = async (calls) => {
    const results = [];
    const errors = [];

    for (const call of calls) {
      try {
        const result = await apiCall(call.method, call.url, call.data, call.options);
        results.push(result);
      } catch (error) {
        errors.push({ call, error });
      }
    }

    return { results, errors };
  };

  const value = {
    // State
    isOnline,
    apiStatus,
    lastSync,
    cacheStats,
    
    // Methods
    get,
    post,
    put,
    delete: del,
    patch,
    fetchWithCache,
    batchCall,
    refreshAllData,
    clearCache: clearApiCache,
    
    // Utility
    apiCall
  };

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
};

// Hook for real-time data updates
export const useRealtimeData = (url, interval = 30000) => {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await get(url);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [get, url]);

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Set up interval for real-time updates
    const intervalId = setInterval(fetchData, interval);

    return () => clearInterval(intervalId);
  }, [fetchData, interval]);

  return { data, loading, error, refetch: fetchData };
};

export default ApiProvider; 